// src/pages/Manutencao/ServicoPopup.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiPlus, FiTrash2, FiFileText, FiUserPlus, FiChevronDown } from 'react-icons/fi';
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import FornecedorPopup from './FornecedorPopup';
import styles from './ServicoPopup.module.css';

const ServicoPopup = ({ onClose, onSave, servico }) => {
    const [titulo, setTitulo] = useState('');
    const [itens, setItens] = useState([]);
    const [newItemDesc, setNewItemDesc] = useState('');
    const [isOsMode, setIsOsMode] = useState(false);
    const [fornecedores, setFornecedores] = useState([]);
    const [fornecedorId, setFornecedorId] = useState('');
    const [numeroOs, setNumeroOs] = useState('');
    const [dataEmissao, setDataEmissao] = useState('');
    const [observacoesGerais, setObservacoesGerais] = useState('');
    const [isFornecedorPopupOpen, setIsFornecedorPopupOpen] = useState(false);

    const fetchFornecedores = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/fornecedores`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setFornecedores(response.data);
        } catch (error) {
            console.error("Erro ao buscar fornecedores:", error);
        }
    }, []);

    useEffect(() => {
        fetchFornecedores();
    }, [fetchFornecedores]);

    useEffect(() => {
        if (servico) {
            setTitulo(servico.titulo || '');
            setItens((servico.itens || []).map(item => ({
                descricao: item.descricao || '',
                quantidade: item.quantidade || '',
                unidade: item.unidade || '',
                valor_unitario: item.valor_unitario || ''
            })));

            if (servico.fornecedor_id) {
                setIsOsMode(true);
                setFornecedorId(servico.fornecedor_id);
                setNumeroOs(servico.numero_os || '');
                setDataEmissao(servico.data_emissao ? new Date(servico.data_emissao).toISOString().split('T')[0] : '');
                setObservacoesGerais(servico.observacoes_gerais || '');
            }
        } else {
            setDataEmissao(new Date().toISOString().split('T')[0]);
        }
    }, [servico, fetchFornecedores]);

    useEffect(() => {
        if (isOsMode && !numeroOs && !servico?.id) {
            const year = new Date().getFullYear();
            const timestamp = Date.now();
            setNumeroOs(`OS-${year}-${timestamp}`);
        } else if (!isOsMode && !servico?.id) {
            setNumeroOs('');
        }
    }, [isOsMode, numeroOs, servico]);

    const handleFornecedorSaveSuccess = (novoFornecedor) => {
        fetchFornecedores().then(() => {
            setFornecedorId(novoFornecedor.id);
        });
    };
    
    const handleAddItem = (e) => {
        e.preventDefault();
        if (newItemDesc.trim()) {
            setItens([...itens, { descricao: newItemDesc.trim(), quantidade: 1, unidade: 'UN', valor_unitario: 0 }]);
            setNewItemDesc('');
        }
    };

    const handleRemoveItem = (indexToRemove) => {
        Swal.fire({
            title: 'Tem certeza?',
            text: "Você não poderá reverter a exclusão deste item!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1e8449',
            cancelButtonColor: '#dc2626',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        }).then((result) => {
            if (result.isConfirmed) {
                setItens(prevItens => prevItens.filter((_, index) => index !== indexToRemove));
            }
        });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...itens];
        newItems[index][field] = value;
        setItens(newItems);
    };

    const handleSave = () => {
        if (!titulo.trim()) {
            Swal.fire('Atenção', 'O campo "Título do Serviço" é obrigatório.', 'warning');
            return;
        }

        const servicoData = {
            id: servico?.id,
            titulo,
            itens: itens.map(item => ({
                ...item,
                quantidade: parseFloat(item.quantidade) || null,
                valor_unitario: parseFloat(item.valor_unitario) || null,
            })),
            fornecedor_id: isOsMode ? fornecedorId : null,
            numero_os: isOsMode ? numeroOs : null,
            data_emissao: isOsMode ? dataEmissao : null,
            observacoes_gerais: isOsMode ? observacoesGerais : null,
        };

        onSave(servicoData);
        onClose();
    };
    
    const totalOs = isOsMode ? itens.reduce((acc, item) => acc + (parseFloat(item.quantidade) || 0) * (parseFloat(item.valor_unitario) || 0), 0) : 0;

    return (
        <>
            <div className={styles.popupOverlay}>
                <div className={styles.popupContent}>
                    <div className={styles.popupHeader}>
                        <h2>{servico ? 'Editar Serviço / OS' : 'Criar Novo Serviço / OS'}</h2>
                        <button onClick={onClose} className={styles.closeButton}><FiX /></button>
                    </div>
                    <div className={styles.popupBody}>
                        <div className={styles.formContainer}>
                            <input type="text" className={styles.titleInput} placeholder="Título do Serviço (ex: Checklist Pré-Viagem)" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
                            <div className={styles.osToggle}>
                                <input type="checkbox" id="osMode" checked={isOsMode} onChange={() => setIsOsMode(!isOsMode)} />
                                <label htmlFor="osMode">Atribuir Fornecedor (Gerar Ordem de Serviço)</label>
                            </div>
                            <div className={`${styles.osDetailsContainer} ${isOsMode ? styles.osDetailsContainerOpen : ''}`}>
                                <div className={styles.osDetails}>
                                    <label>Fornecedor</label>
                                    <div className={styles.fornecedorWrapper}>
                                        <div className={styles.selectWrapper}>
                                            <select value={fornecedorId} onChange={(e) => setFornecedorId(e.target.value)} className={styles.osInput}>
                                                <option value="">-- Selecione um Fornecedor --</option>
                                                {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome_empresa}</option>)}
                                            </select>
                                            <FiChevronDown className={styles.selectIcon} />
                                        </div>
                                        <button onClick={() => setIsFornecedorPopupOpen(true)} className={styles.newFornecedorButton}><FiUserPlus size={18}/> Novo</button>
                                    </div>
                                    <div className={styles.formGrid}>
                                        <div>
                                            <label htmlFor="numeroOs">Nº da OS</label>
                                            <input id="numeroOs" type="text" value={numeroOs} readOnly className={styles.osInput} />
                                        </div>
                                        <div>
                                            <label htmlFor="dataEmissao">Data de Emissão</label>
                                            <input id="dataEmissao" type="date" value={dataEmissao} onChange={(e) => setDataEmissao(e.target.value)} className={styles.osInput} />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="observacoes">Observações Gerais</label>
                                        <textarea id="observacoes" placeholder="Adicione detalhes importantes..." value={observacoesGerais} onChange={(e) => setObservacoesGerais(e.target.value)} className={styles.osTextarea} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.itemsContainer}>
                            <h3 className={styles.itemsHeader}><FiFileText /> Itens do Serviço</h3>
                            <form onSubmit={handleAddItem} className={styles.addItemForm}>
                                <input type="text" className={styles.itemInput} placeholder="Adicionar descrição do novo item" value={newItemDesc} onChange={(e) => setNewItemDesc(e.target.value)} />
                                <button type="submit" className={styles.addButton}><FiPlus /> Adicionar</button>
                            </form>
                            <div className={styles.itemsTableWrapper}>
                                <div className={styles.itemsTableContainer}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Descrição</th>
                                                {isOsMode && <>
                                                    <th className={styles.colNumber}>Qtd.</th>
                                                    <th className={styles.colText}>Unid.</th>
                                                    <th className={styles.colCurrency}>Vl. Unitário</th>
                                                </>}
                                                <th className={styles.colAction}>Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itens.length > 0 ? (
                                                itens.map((item, index) => (
                                                    <tr key={index}>
                                                        <td><input type="text" value={item.descricao} onChange={(e) => handleItemChange(index, 'descricao', e.target.value)} /></td>
                                                        {isOsMode && <>
                                                            <td><input type="number" value={item.quantidade} onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)} /></td>
                                                            <td><input type="text" value={item.unidade} onChange={(e) => handleItemChange(index, 'unidade', e.target.value)} /></td>
                                                            <td><input type="number" step="0.01" value={item.valor_unitario} onChange={(e) => handleItemChange(index, 'valor_unitario', e.target.value)} /></td>
                                                        </>}
                                                        <td><button onClick={() => handleRemoveItem(index)} className={styles.removeButton}><FiTrash2 /></button></td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={isOsMode ? 5 : 2} className={styles.emptyMessage}>
                                                        Nenhum item adicionado ainda.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.popupFooter}>
                        <div className={styles.totalDisplay}>
                            {isOsMode && <>Total: <strong>{totalOs.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></>}
                        </div>
                        <button onClick={handleSave} className={styles.saveButton}>Salvar</button>
                    </div>
                </div>
            </div>
            {isFornecedorPopupOpen && (
                <FornecedorPopup 
                    onClose={() => setIsFornecedorPopupOpen(false)} 
                    onSaveSuccess={handleFornecedorSaveSuccess}
                />
            )}
        </>
    );
};

export default ServicoPopup;